import { ReactNode } from 'react';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import {
  ActionProp,
  ActionsDesktop,
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { SubscriptionsHelpLink } from 'domains/Catalog/Subscriptions/components';
import { MetaplansListEmpty } from '../MetaplansListEmpty';

interface MetaplansListPageBaseProps {
  skeleton: ReactNode;
  isLoading: boolean;
  isEmpty: boolean;
  isError: boolean;
  children: ReactNode;
  onRetry: () => void;
  allProductsInMetaplan?: boolean;
}

export function MetaplansListPageBase({
  skeleton,
  isLoading,
  isEmpty,
  isError,
  children,
  onRetry,
  allProductsInMetaplan: allProductsInMetaplan = false,
}: Readonly<MetaplansListPageBaseProps>): JSX.Element {
  const t = useTranslationCatalog();
  const { goTo } = useNavegate();
  const title = t('subscriptions.title');

  const handleCreatePlan = () => {
    if (!allProductsInMetaplan) {
      goTo(CATALOG_ROUTES.newMetaplan);
    }
  };

  const createMetaplanAction: ActionProp = {
    children: t('subscriptions.createPlan'),
    icon: PlusCircleIcon,
    onClick: handleCreatePlan,
    appearance: 'default',
    disabled: allProductsInMetaplan,
    tooltip: allProductsInMetaplan
      ? t('subscriptions.createPlanTooltip')
      : undefined,
  };

  const desktopActions: ActionProp[] = [
    {
      ...createMetaplanAction,
      appearance: 'primary',
    },
  ];

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop mainAction={!isError ? createMetaplanAction : undefined} />
      }
      headerContent={
        !isError ? (
          <HeaderContent
            title={title}
            actions={<ActionsDesktop actions={desktopActions} />}
          />
        ) : undefined
      }
      headerFixed
    >
      {isLoading && skeleton}
      {isError && <ErrorScreen onRetry={onRetry} />}
      {isEmpty && <MetaplansListEmpty />}
      {!isLoading && !isError && !isEmpty && children}
      {!isError && <SubscriptionsHelpLink />}
    </IonPageStratus>
  );
}
